import React, { useCallback, useEffect, useState } from 'react';
import {
  deletePreference,
  interpretPreference,
  listPreferences,
  MendsAiPreference,
  PreferenceDraft,
  savePreference,
} from '../services/aiCompanyService';
import { apiErrorMessage } from '../../utils/apiErrorMessage';
import '../styles/aiCompany.css';

/**
 * F4 — Preferências de envio da IA ditas em frase (Configurações › Autonomia da IA). A frase vira regra pela IA, o
 * usuário confere "entendi assim" e só então grava. Vale para os envios automáticos: fora da regra, a mensagem é
 * reagendada para o próximo horário permitido.
 */
export interface PreferencesSectionProps {
  sectorId: number;
}

const EXAMPLES = [
  'nunca envie mensagens depois das 20h',
  'não mande nada no fim de semana',
  'no máximo 2 mensagens por cliente por dia',
];

function when(iso?: string) {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleString('pt-BR', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
  } catch {
    return iso;
  }
}

/** Frase → "entendi assim" → salvar. Usado para nova preferência e para editar uma existente. */
const PreferenceEditor: React.FC<{
  sectorId: number;
  initial?: MendsAiPreference;
  onSaved: () => void;
  onCancel?: () => void;
}> = ({ sectorId, initial, onSaved, onCancel }) => {
  const [phrase, setPhrase] = useState(initial?.phrase || '');
  const [draft, setDraft] = useState<PreferenceDraft | null>(null);
  const [busy, setBusy] = useState<'read' | 'save' | null>(null);
  const [error, setError] = useState<string | null>(null);

  const read = async () => {
    if (!phrase.trim()) return;
    setBusy('read');
    setError(null);
    setDraft(null);
    try {
      setDraft(await interpretPreference(sectorId, phrase.trim()));
    } catch (e: unknown) {
      setError(apiErrorMessage(e, 'Não consegui ler a frase agora. Nada foi gravado.'));
    } finally {
      setBusy(null);
    }
  };

  const save = async () => {
    if (!draft?.understood) return;
    setBusy('save');
    setError(null);
    try {
      await savePreference({ sectorId, id: initial?.id ?? null, phrase: draft.phrase, rules: draft.rules, enabled: initial?.enabled ?? true });
      setPhrase('');
      setDraft(null);
      onSaved();
    } catch (e: unknown) {
      setError(apiErrorMessage(e, 'Não consegui salvar. Nada foi gravado.'));
    } finally {
      setBusy(null);
    }
  };

  const fieldId = `mai-pref-phrase-${initial?.id ?? 'new'}`;
  return (
    <div className="mai-prefs-editor">
      <label className="mai-approval-label" htmlFor={fieldId}>
        {initial ? 'Diga a preferência de novo' : 'Diga como a IA deve (ou não deve) enviar mensagens automáticas'}
      </label>
      <div className="mai-prefs-form">
        <textarea
          id={fieldId}
          className="mai-approval-edit"
          rows={2}
          value={phrase}
          placeholder={`Ex.: "${EXAMPLES[0]}"`}
          onChange={(e) => { setPhrase(e.target.value); setDraft(null); }}
          disabled={busy !== null}
        />
        <button type="button" className="mai-btn mai-btn-soft" disabled={busy !== null || !phrase.trim()} onClick={() => void read()}>
          {busy === 'read' ? 'Lendo…' : 'Ler a frase'}
        </button>
        {onCancel && (
          <button type="button" className="mai-btn mai-btn-ghost" disabled={busy !== null} onClick={onCancel}>Cancelar</button>
        )}
      </div>
      {draft && !draft.understood && (
        <p className="mai-form-error" role="alert">{draft.notUnderstoodReason || 'Não entendi como regra de envio.'}</p>
      )}
      {draft?.understood && (
        <div className="mai-bulk mai-pref-confirm" role="group" aria-label="Como entendi a preferência">
          <p className="mai-bulk-head">Entendi assim:</p>
          <ul className="mai-pref-lines">{draft.lines.map((l) => <li key={l}>{l}</li>)}</ul>
          {draft.assumptions.length > 0 && (
            <ul className="mai-reg-confirm-warn">{draft.assumptions.map((a) => <li key={a}>{a}</li>)}</ul>
          )}
          <div className="mai-btn-row">
            <button type="button" className="mai-btn" disabled={busy !== null} onClick={() => void save()}>
              {busy === 'save' ? 'Salvando…' : 'Está certo, salvar'}
            </button>
            <button type="button" className="mai-btn mai-btn-ghost" disabled={busy !== null} onClick={() => setDraft(null)}>
              Corrigir a frase
            </button>
          </div>
        </div>
      )}
      {error && <p className="mai-form-error" role="alert">{error}</p>}
    </div>
  );
};

export const PreferencesSection: React.FC<PreferencesSectionProps> = ({ sectorId }) => {
  const [items, setItems] = useState<MendsAiPreference[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [editing, setEditing] = useState<number | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<number | null>(null);
  const [busyId, setBusyId] = useState<number | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      setItems((await listPreferences(sectorId)) || []);
    } catch (e: unknown) {
      setLoadError(apiErrorMessage(e, 'Falha ao carregar as preferências.'));
    } finally {
      setLoading(false);
    }
  }, [sectorId]);

  useEffect(() => { void load(); }, [load]);

  const toggle = async (p: MendsAiPreference) => {
    setBusyId(p.id);
    setError(null);
    try {
      await savePreference({ sectorId, id: p.id, phrase: p.phrase, rules: p.rules, enabled: !p.enabled });
      await load();
    } catch (e: unknown) {
      setError(apiErrorMessage(e, 'Não consegui mudar. Nada foi alterado.'));
    } finally {
      setBusyId(null);
    }
  };

  const remove = async (p: MendsAiPreference) => {
    setBusyId(p.id);
    setError(null);
    try {
      await deletePreference(sectorId, p.id);
      setConfirmDelete(null);
      await load();
    } catch (e: unknown) {
      setError(apiErrorMessage(e, 'Não consegui apagar.'));
    } finally {
      setBusyId(null);
    }
  };

  return (
    <section className="mai-section" style={{ marginTop: 36 }} aria-labelledby="mai-prefs-title">
      <div className="mai-section-label" id="mai-prefs-title">Preferências de envio</div>
      <p className="mai-card-detail" style={{ marginBottom: 12 }}>
        Regras ditas em frase para os envios automáticos da IA (follow-ups, retornos combinados e mensagens programadas).
        Fora da regra, a mensagem não sai na hora: é reagendada para o próximo horário permitido. Resposta a quem acabou
        de escrever continua saindo, a menos que a frase diga que vale para respostas. Também dá para dizer no Registrar.
      </p>

      <div className="mai-card" style={{ marginBottom: 12 }}>
        <PreferenceEditor sectorId={sectorId} onSaved={() => void load()} />
      </div>

      {loading && <div className="mai-empty">Carregando…</div>}
      {!loading && loadError && (
        <div className="mai-form-error" role="alert">
          {loadError}{' '}
          <button type="button" className="mai-linkish" onClick={() => void load()}>Tentar de novo</button>
        </div>
      )}
      {!loading && !loadError && items.length === 0 && (
        <div className="mai-empty">Nenhuma preferência ainda. Exemplos: {EXAMPLES.map((e) => `“${e}”`).join(', ')}.</div>
      )}
      {items.map((p) => (
        <div key={p.id} className={`mai-card mai-prefs-item${p.enabled ? '' : ' mai-prefs-off'}`} style={{ marginBottom: 8 }}>
          <div className="mai-prefs-item-head">
            <div>
              <p className="mai-prefs-item-phrase">“{p.phrase}”</p>
              <p className="mai-prefs-item-meta">
                {p.enabled ? 'Ativa' : 'Pausada'} · {p.createdByName ? `por ${p.createdByName} · ` : ''}atualizada {when(p.updatedAt)}
              </p>
            </div>
            <div className="mai-btn-row">
              <button type="button" className="mai-linkish" disabled={busyId === p.id} onClick={() => void toggle(p)}>
                {p.enabled ? 'Pausar' : 'Ativar'}
              </button>
              <button type="button" className="mai-linkish" disabled={busyId === p.id} onClick={() => setEditing(editing === p.id ? null : p.id)}>
                Editar
              </button>
              {confirmDelete === p.id ? (
                <>
                  <button type="button" className="mai-linkish" disabled={busyId === p.id} onClick={() => void remove(p)}>Confirmar apagar</button>
                  <button type="button" className="mai-linkish" onClick={() => setConfirmDelete(null)}>Não</button>
                </>
              ) : (
                <button type="button" className="mai-linkish" disabled={busyId === p.id} onClick={() => setConfirmDelete(p.id)}>Apagar</button>
              )}
            </div>
          </div>
          <ul className="mai-pref-lines" style={{ margin: 0, paddingLeft: 18 }}>
            {p.understood.map((l) => <li key={l}>{l}</li>)}
          </ul>
          {editing === p.id && (
            <PreferenceEditor
              sectorId={sectorId}
              initial={p}
              onSaved={() => { setEditing(null); void load(); }}
              onCancel={() => setEditing(null)}
            />
          )}
        </div>
      ))}
      {error && <p className="mai-form-error" role="alert">{error}</p>}
    </section>
  );
};
